import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/api'

interface SubscriptionPlan {
  id: string
  code: string
  name: string
  description: string
  monthly_price: number
  quarterly_price: number | null
  semi_annual_price: number | null
  annual_price: number | null
  features: Record<string, boolean>
  limits: {
    products: number | null
    users: number | null
    locations: number | null
    monthly_transactions: number | null
    storage_mb: number | null
  }
  active?: boolean
}

interface RawSubscriptionPlan {
  id: string
  code?: string
  plan_code?: string
  name?: string
  plan_name?: string
  description?: string
  monthly_price: number
  quarterly_price?: number | null
  semi_annual_price?: number | null
  annual_price?: number | null
  features?: Record<string, boolean>
  limits?: {
    products: number | null
    users: number | null
    locations: number | null
    monthly_transactions: number | null
    storage_mb: number | null
  }
  active?: boolean
}

interface SubscriptionStatus {
  has_subscription: boolean
  status: 'none' | 'free' | 'trial' | 'active' | 'expired' | 'cancelled' | 'suspended'
  subscription_id?: string
  plan?: {
    id: string
    name: string
    code: string
    price: number
    features: Record<string, boolean>
  }
  billing?: {
    starts_at: string
    ends_at: string
    next_billing_date: string
    days_remaining: number
    auto_renew: boolean
  }
  usage?: Record<string, { current: number; limit: number | null; percentage: number }>
  trial_available?: boolean
}

function normaliseStatus(raw: any): SubscriptionStatus | null {
  if (!raw || typeof raw !== 'object') {
    return null
  }

  if ('has_subscription' in raw || 'trial_available' in raw || 'billing' in raw) {
    return {
      has_subscription: Boolean(raw.has_subscription),
      status: raw.status ?? 'none',
      subscription_id: raw.subscription_id,
      plan: raw.plan
        ? {
            id: raw.plan.id ?? '',
            name: raw.plan.name ?? raw.plan.plan_name ?? '',
            code: raw.plan.code ?? raw.plan.plan_code ?? '',
            price: Number(raw.plan.price ?? raw.plan.monthly_price ?? 0),
            features: raw.plan.features ?? {},
          }
        : undefined,
      billing: raw.billing
        ? {
            starts_at: raw.billing.starts_at ?? '',
            ends_at: raw.billing.ends_at ?? '',
            next_billing_date: raw.billing.next_billing_date ?? '',
            days_remaining: Number(raw.billing.days_remaining ?? 0),
            auto_renew: Boolean(raw.billing.auto_renew),
          }
        : undefined,
      usage: raw.usage ?? undefined,
      trial_available: raw.trial_available ?? false,
    }
  }

  const legacyStatus = String(raw.status ?? 'none') as SubscriptionStatus['status']
  const hasSubscription = ['active', 'trial'].includes(legacyStatus)

  return {
    has_subscription: hasSubscription,
    status: legacyStatus,
    plan: raw.plan_name || raw.plan_code
      ? {
          id: '',
          name: raw.plan_name ?? '',
          code: raw.plan_code ?? '',
          price: 0,
          features: {},
        }
      : undefined,
    billing: raw.ends_at || raw.days_remaining !== undefined
      ? {
          starts_at: '',
          ends_at: raw.ends_at ?? '',
          next_billing_date: raw.ends_at ?? '',
          days_remaining: Number(raw.days_remaining ?? 0),
          auto_renew: true,
        }
      : undefined,
    trial_available: Boolean(raw.is_trial) ? false : true,
  }
}

function normalisePlan(raw: RawSubscriptionPlan): SubscriptionPlan {
  return {
    id: raw.id,
    code: raw.code ?? raw.plan_code ?? '',
    name: raw.name ?? raw.plan_name ?? '',
    description: raw.description ?? '',
    monthly_price: raw.monthly_price ?? 0,
    quarterly_price: raw.quarterly_price ?? null,
    semi_annual_price: raw.semi_annual_price ?? null,
    annual_price: raw.annual_price ?? null,
    features: (typeof raw.features === 'string' ? JSON.parse(raw.features) : raw.features) ?? {},
    limits: raw.limits ?? {
      products: null,
      users: null,
      locations: null,
      monthly_transactions: null,
      storage_mb: null,
    },
    active: Boolean(raw.active),
  }
}

export const useSubscriptionStore = defineStore('subscription', () => {
  const status        = ref<SubscriptionStatus | null>(null)
  const plans         = ref<SubscriptionPlan[]>([])
  const modules       = ref<SubscriptionPlan[]>([])
  const loading       = ref(false)
  const plansLoading  = ref(false)
  const error         = ref<string | null>(null)
  const paymentPending  = ref(false)
  const lastPaymentId   = ref<string | null>(null)

  // Computed
  const isSubscribed = computed(() => {
    return status.value ? ['free', 'active', 'trial'].includes(status.value.status) : false
  })

  const isTrialActive = computed(() => {
    return status.value?.status === 'trial'
  })

  const isExpired = computed(() => {
    return status.value?.status === 'expired'
      || status.value?.status === 'none'
      || !status.value?.has_subscription
  })

  const trialDaysRemaining = computed(() => {
    if (!isTrialActive.value || !status.value?.billing?.days_remaining) return 0
    return status.value.billing.days_remaining
  })

  const currentPlan = computed(() => {
    return status.value?.plan || null
  })

  const needsSubscription = computed(() => {
    if (!status.value) return true
    if (status.value.status === 'free') return false
    return !status.value.has_subscription
      || ['expired', 'cancelled', 'suspended', 'none'].includes(status.value.status)
  })

  const trialAvailable = computed(() => {
    return status.value?.trial_available ?? true
  })

  function setStatus(raw: any) {
    status.value = normaliseStatus(raw)
  }

  // Actions
  async function fetchStatus() {
    loading.value = true
    error.value = null
    try {
      const { data } = await api.get('/subscription/status')
      if (data.success) {
        setStatus(data.data)
      } else {
        error.value = data.error || 'Unexpected response from /subscription/status'
      }
    } catch (e: any) {
      const httpStatus = e.response?.status
      const responseData = e.response?.data

      if (httpStatus === 402) {
        status.value = {
          has_subscription: false,
          status: 'none',
          trial_available: true
        }
      } else if (httpStatus === 401) {
        error.value = 'Not authenticated. Please log in again.'
      } else if (httpStatus === 403) {
        error.value = 'Access denied.'
      } else if (!httpStatus) {
        error.value = 'Network error — could not reach the server.'
      } else {
        error.value = responseData?.error || `Unexpected error (HTTP ${httpStatus})`
      }
    } finally {
      loading.value = false
    }
  }

  async function fetchPlans() {
    plansLoading.value = true
    error.value = null
    try {
      const { data } = await api.get('/subscription/plans-new')
      if (data.success && Array.isArray(data.data)) {
        plans.value = (data.data as RawSubscriptionPlan[]).map(normalisePlan)
      } else {
        error.value = 'Failed to load subscription plans. Please refresh.'
      }
    } catch (e: any) {
      error.value = 'Failed to load subscription plans. Please refresh and try again.'
    } finally {
      plansLoading.value = false
    }
  }

  async function fetchModules() {
    try {
      const { data } = await api.get('/subscription/modules')
      if (data.success && Array.isArray(data.data)) {
        modules.value = (data.data as RawSubscriptionPlan[]).map(normalisePlan)
      }
    } catch (e: any) {
      error.value = e.response?.data?.error || 'Failed to load subscription modules.'
    }
  }

  async function startTrial() {
    loading.value = true
    error.value = null
    try {
      const { data } = await api.post('/subscription/trial')
      if (data.success) {
        await fetchStatus()
        return true
      }
      return false
    } catch (e: any) {
      error.value = e.response?.data?.error || 'Failed to start trial'
      return false
    } finally {
      loading.value = false
    }
  }

  async function subscribe(planId: string, billingPeriod: string = 'monthly', phoneNumber?: string) {
    paymentPending.value = true
    error.value = null
    try {
      const { data } = await api.post('/subscription/subscribe', {
        plan_id: planId,
        billing_period: billingPeriod,
        phone_number: phoneNumber
      })
      if (data.success) {
        lastPaymentId.value = data.payment?.payment_id || null
        await fetchStatus()
        return data
      }
      return null
    } catch (e: any) {
      const responseData = e.response?.data
      const validationErrors = responseData?.details || responseData?.validation_errors
      const firstValidationMessage = validationErrors && typeof validationErrors === 'object'
        ? Object.values(validationErrors).flat().find(Boolean)
        : null

      error.value = firstValidationMessage || responseData?.error || responseData?.message || 'Failed to process subscription'
      return null
    } finally {
      paymentPending.value = false
    }
  }

  async function subscribeModule(moduleId: string, billingPeriod: string = 'monthly', phoneNumber?: string) {
    paymentPending.value = true
    error.value = null
    try {
      const { data } = await api.post('/subscription/modules/subscribe', {
        module_id: moduleId,
        billing_period: billingPeriod,
        phone_number: phoneNumber,
      })
      if (data.success) {
        lastPaymentId.value = data.payment?.payment_id || null
        return data
      }
      return null
    } catch (e: any) {
      error.value = e.response?.data?.error || e.response?.data?.message || 'Failed to process module subscription'
      return null
    } finally {
      paymentPending.value = false
    }
  }

  async function waitForPayment(paymentId: string, timeoutMs = 90000) {
    const deadline = Date.now() + timeoutMs
    while (Date.now() < deadline) {
      try {
        const { data } = await api.get(`/subscription/payments/${paymentId}/status`)
        const payment = data.data
        if (payment?.status === 'completed') {
          await Promise.all([fetchStatus(), fetchModules()])
          const { useFeatureGate } = await import('@/composables/useFeatureGate')
          const featureGate = useFeatureGate(false)
          featureGate.resetGates()
          await featureGate.fetchGates()
          return payment
        }
        if (payment?.status === 'failed') {
          error.value = payment.failure_reason || 'M-Pesa payment was not completed.'
          return payment
        }
      } catch {
        // A transient network failure should not abandon an otherwise pending STK request.
      }
      await new Promise(resolve => window.setTimeout(resolve, 2000))
    }

    return { status: 'pending' }
  }

  async function cancelSubscription() {
    loading.value = true
    try {
      const { data } = await api.post('/subscription/cancel')
      if (data.success) {
        await fetchStatus()
        return true
      }
      return false
    } catch (e: any) {
      error.value = e.response?.data?.error || 'Failed to cancel subscription'
      return false
    } finally {
      loading.value = false
    }
  }

  function $reset() {
    status.value        = null
    plans.value         = []
    modules.value       = []
    loading.value       = false
    error.value         = null
    paymentPending.value  = false
    lastPaymentId.value   = null
  }

  return {
    status,
    plans,
    modules,
    loading,
    plansLoading,
    error,
    paymentPending,
    lastPaymentId,
    isSubscribed,
    isTrialActive,
    isExpired,
    trialDaysRemaining,
    currentPlan,
    needsSubscription,
    trialAvailable,
    setStatus,
    fetchStatus,
    fetchPlans,
    fetchModules,
    startTrial,
    subscribe,
    subscribeModule,
    waitForPayment,
    cancelSubscription,
    $reset,
  }
})
